import { supabase } from './SupabaseConfig';
import { PosSessionRepository } from '../../domain/pos/ports/PosSessionRepository';
import { PosSession } from '../../domain/pos/entities/PosSession';
import { InfrastructureError } from '@core/errors/AppError';

export class SupabasePosSessionRepository implements PosSessionRepository {
  async createSession(ownerId: string, session: Partial<PosSession>): Promise<string> {
    try {
      const { data, error } = await supabase
        .from('pos_sessions')
        .insert({
          owner_id: ownerId,
          staff_id: session.staffId,
          staff_name: session.staffName,
          staff_email: session.staffEmail,
          location_id: session.locationId,
          location_name: session.locationName,
          device_type: session.deviceType || 'pc',
          status: 'online',
          last_activity_at: new Date().toISOString(),
          started_at: session.startedAt ? new Date(session.startedAt).toISOString() : new Date().toISOString(),
          shift_id: session.shiftId
        })
        .select('id')
        .single();

      if (error) throw new InfrastructureError(`Failed to create POS session: ${error.message}`, 'SupabasePosSessionRepository');
      return data.id;
    } catch (err) {
      console.error('[SupabasePosSessionRepository] createSession error:', err);
      throw err;
    }
  }

  async updateHeartbeat(ownerId: string, sessionId: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('pos_sessions')
        .update({
          last_activity_at: new Date().toISOString(),
          status: 'online'
        })
        .eq('id', sessionId)
        .eq('owner_id', ownerId);

      if (error) throw new InfrastructureError(`Failed to update heartbeat: ${error.message}`, 'SupabasePosSessionRepository');
    } catch (err) {
      console.error('[SupabasePosSessionRepository] updateHeartbeat error:', err);
      throw err;
    }
  }

  async setOffline(ownerId: string, sessionId: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('pos_sessions')
        .update({
          status: 'offline',
          last_activity_at: new Date().toISOString()
        })
        .eq('id', sessionId)
        .eq('owner_id', ownerId);

      if (error) throw new InfrastructureError(`Failed to set session offline: ${error.message}`, 'SupabasePosSessionRepository');
    } catch (err) {
      console.error('[SupabasePosSessionRepository] setOffline error:', err);
      throw err;
    }
  }

  listenSessions(ownerId: string, callback: (sessions: PosSession[]) => void): () => void {
    const channel = supabase
      .channel(`pos_sessions_owner_${ownerId}`)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'pos_sessions',
        filter: `owner_id=eq.${ownerId}`
      }, async () => {
        const { data, error } = await supabase
          .from('pos_sessions')
          .select('*')
          .eq('owner_id', ownerId)
          .order('last_activity_at', { ascending: false });
        
        if (!error && data) {
          callback(data.map(this.mapToEntity));
        }
      })
      .subscribe();

    // Initial fetch
    supabase
      .from('pos_sessions')
      .select('*')
      .eq('owner_id', ownerId)
      .order('last_activity_at', { ascending: false })
      .then(({ data, error }) => {
        if (!error && data) callback(data.map(this.mapToEntity));
      });

    return () => {
      channel.unsubscribe();
    };
  }

  private mapToEntity(d: any): PosSession {
    return {
      id: d.id,
      staffId: d.staff_id,
      staffName: d.staff_name,
      staffEmail: d.staff_email,
      locationId: d.location_id,
      locationName: d.location_name,
      deviceType: d.device_type,
      status: d.status,
      lastActivityAt: d.last_activity_at,
      startedAt: d.started_at,
      shiftId: d.shift_id
    };
  }
}
